// Form primitives for the server and template cards.
//
// Every one of these emits exactly two cells -- a label and a value -- because
// they are rendered straight into `Block`'s `grid-cols-[max-content,1fr]`. That
// is what keeps the value column aligned down the whole card no matter how long
// the individual labels are; a field that wrapped itself in a div would opt out
// of the shared grid and drift.
//
// The split that matters here is Detail vs. the rest. A read-only fact (host,
// slot, when it was created) is plain text: boxing it makes it look editable
// and adds a border for no information. Anything you can actually change gets a
// real input box, visible without hovering, so the card reads as a form.

import { Label } from '~/team_tools/components/Label'

const BOX = 'input-team-tools w-full py-1 px-2 text-sm text-left'

export const Detail = ({ label, value, className }: { label: string; value: any; className?: string }) => (
    <>
        <Label label={label} />
        <div className={['text-left min-w-0 truncate', className].filter(Boolean).join(' ')}>{value}</div>
    </>
)

export const TextField = ({
    label,
    value,
    onChange,
    placeholder,
    disabled,
    type = 'text',
}: {
    label: string
    value: any
    onChange: (v: string) => void
    placeholder?: string
    disabled?: boolean
    type?: string
}) => (
    <>
        <Label label={label} />
        <input
            type={type}
            value={value ?? ''}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className={`${BOX} disabled:opacity-40`}
        />
    </>
)

export const SelectField = ({
    label,
    value,
    options,
    onChange,
    disabled,
}: {
    label: string
    value: any
    options: Array<{ value: any; label: string }>
    onChange: (v: any) => void
    disabled?: boolean
}) => (
    <>
        <Label label={label} />
        <select
            value={value ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className={`${BOX} disabled:opacity-40`}
        >
            {options.map((o) => (
                <option key={String(o.value)} value={o.value} className='bg-neutral-900'>
                    {o.label}
                </option>
            ))}
        </select>
    </>
)

/**
 * A label plus arbitrary content, for the cases the four typed fields do not
 * cover -- a checkbox group, a derived readout, a preview line. Still exactly
 * two cells, so it stays inside `Block`'s grid; unlike `Detail` it does not
 * truncate, because the content is expected to wrap.
 */
export const CustomField = ({
    label,
    children,
    hint,
}: {
    label: string
    children: any
    hint?: string
}) => (
    <>
        <Label label={label} />
        <div className='flex flex-col gap-1 min-w-0 text-left'>
            {children}
            {hint && <span className='text-[10px] text-white/40'>{hint}</span>}
        </div>
    </>
)

export const CheckField = ({
    label,
    checked,
    onChange,
    disabled,
    hint,
}: {
    label: string
    checked: boolean
    onChange: (v: boolean) => void
    disabled?: boolean
    /** What being checked means, spelled out -- a bare tick is ambiguous. */
    hint?: string
}) => (
    <>
        <Label label={label} />
        <label className='flex flex-row items-center gap-2 text-sm cursor-pointer min-w-0'>
            <input
                type='checkbox'
                checked={!!checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                className='accent-violet-500 disabled:opacity-40 shrink-0'
            />
            {hint && <span className='opacity-60 text-xs truncate'>{hint}</span>}
        </label>
    </>
)
